import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast';
import { supabase } from '../../lib/supabase';

const FEATURES = [
  { key: 'ai_content', label: 'AI Content Generation' },
  { key: 'publishing', label: 'Social Publishing' },
  { key: 'ui_experience', label: 'UI / Experience' },
  { key: 'writing_styles', label: 'Writing Styles' },
  { key: 'analytics', label: 'Analytics & Insights' },
];

const WHATSAPP_NUMBER = '919761100842';
const SUPPORT_EMAIL = 'support@postrequest.social';

const StarRating = ({ value, onChange, disabled }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={disabled}
        onClick={() => onChange(star === value ? 0 : star)}
        className="transition-colors disabled:cursor-not-allowed"
      >
        <Icon
          name="Star"
          size={20}
          className={star <= value ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}
        />
      </button>
    ))}
  </div>
);

const Feedback = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('general');
  const [ratings, setRatings] = useState({});
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pastFeedback, setPastFeedback] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const tabs = [
    { key: 'general', label: 'General Feedback', icon: 'MessageSquare' },
    { key: 'feature_request', label: 'Feature Request', icon: 'Lightbulb' },
    { key: 'bug_report', label: 'Bug Report', icon: 'Bug' },
  ];

  useEffect(() => {
    if (!user) return;
    const loadHistory = async () => {
      setLoadingHistory(true);
      try {
        const { data } = await supabase
          .from('user_feedback')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);
        setPastFeedback(data || []);
      } catch { /* ignore */ }
      setLoadingHistory(false);
    };
    loadHistory();
  }, [user, submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please enter your feedback before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('user_feedback').insert({
        user_id: user.id,
        feedback_type: activeTab,
        feature_ratings: activeTab === 'general' ? ratings : {},
        message: message.trim(),
      });
      if (error) throw error;
      toast.success('Thank you for your feedback!');
      setMessage('');
      setRatings({});
      setSubmitted((prev) => !prev);
    } catch (err) {
      toast.error(err?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const placeholders = {
    general: 'Tell us what you think about POSTRequest — what works well, what could be better...',
    feature_request: 'Describe the feature you\'d like to see. What problem would it solve for you?',
    bug_report: 'Describe the bug: what happened, what you expected, and steps to reproduce it.',
  };

  const typeLabel = { general: 'General', feature_request: 'Feature Request', bug_report: 'Bug Report' };
  const typeColor = { general: 'text-violet-400', feature_request: 'text-amber-400', bug_report: 'text-red-400' };

  return (
    <div className="min-h-screen bg-background">
      <main className="px-4 md:px-6 lg:px-8 py-8 pb-12">
        <div className="max-w-2xl mx-auto w-full">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">Feedback</h1>
            <p className="text-sm text-muted-foreground">
              Help us make POSTRequest better. Your input shapes our roadmap.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-violet-500/15 text-violet-300 border border-violet-500/30'
                    : 'bg-card text-muted-foreground border border-border hover:border-border'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form Card */}
          <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-5 mb-6">
            {/* Feature Ratings — only for general feedback */}
            {activeTab === 'general' && (
              <div className="mb-5">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Star" size={15} className="text-violet-400" />
                  Rate Our Features
                </h2>
                <div className="space-y-3">
                  {FEATURES.map((feature) => (
                    <div key={feature.key} className="flex items-center justify-between">
                      <span className="text-sm text-foreground">{feature.label}</span>
                      <StarRating
                        value={ratings[feature.key] || 0}
                        onChange={(val) => setRatings((prev) => ({ ...prev, [feature.key]: val }))}
                        disabled={submitting}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                {activeTab === 'general' && 'Your Feedback'}
                {activeTab === 'feature_request' && 'Feature Description'}
                {activeTab === 'bug_report' && 'Bug Details'}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={placeholders[activeTab]}
                disabled={submitting}
                rows={5}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !message.trim()}
              className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-semibold bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
            >
              {submitting ? (
                <><Icon name="Loader2" size={14} className="animate-spin" /> Submitting...</>
              ) : (
                <><Icon name="Send" size={14} /> Submit Feedback</>
              )}
            </button>
          </form>

          {/* Contact Us Card */}
          <div className="bg-card border border-border rounded-xl p-5 mb-6">
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Phone" size={15} className="text-violet-400" />
              Reach Out Directly
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Prefer a direct conversation? We're happy to hear from you.
            </p>
            <div className="flex gap-3">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi! I have feedback about POSTRequest.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
              >
                <Icon name="MessageCircle" size={16} />
                WhatsApp
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Feedback%20for%20POSTRequest`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors"
              >
                <Icon name="Mail" size={16} />
                Email
              </a>
            </div>
          </div>

          {/* Past Feedback */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="History" size={15} className="text-violet-400" />
              Your Past Feedback
            </h2>
            {loadingHistory ? (
              <div className="flex justify-center py-6">
                <Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" />
              </div>
            ) : pastFeedback.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">
                No feedback submitted yet. Be the first to share your thoughts!
              </p>
            ) : (
              <div className="space-y-3">
                {pastFeedback.map((item) => (
                  <div key={item.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-xs font-medium ${typeColor[item.feedback_type] || 'text-muted-foreground'}`}>
                        {typeLabel[item.feedback_type] || item.feedback_type}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground line-clamp-3">{item.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Feedback;
